import { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { addDoc, collection } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useAuth } from '../hooks/useAuth.js'
import ModalPortal from './ModalPortal.jsx'

// Constants for better maintainability and performance
const FEEDBACK_CATEGORIES = [
  { value: 'general', label: 'General Feedback', icon: 'bi-chat-dots' },
  { value: 'bug', label: 'Bug Report', icon: 'bi-bug' },
  { value: 'feature', label: 'Feature Request', icon: 'bi-lightbulb' },
  { value: 'usability', label: 'Usability Issue', icon: 'bi-cursor' },
  { value: 'performance', label: 'Performance Issue', icon: 'bi-speedometer2' },
  { value: 'other', label: 'Other', icon: 'bi-three-dots' },
]

const CHAR_LIMITS = {
  subject: 100,
  message: 2000,
}

/**
 * SendFeedback modal component for collecting user feedback
 * Features:
 * - Feedback category selection (bug reports, feature requests, etc.)
 * - Rich text feedback form with validation
 * - Firebase Firestore integration for storage
 * - System information collection for debugging
 * - User context integration with authentication
 */
function SendFeedback({ isOpen, onClose }) {
  const { user } = useAuth()
  const [feedback, setFeedback] = useState({
    category: 'general',
    subject: '',
    message: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Memoized event handlers to prevent unnecessary re-renders
  const handleInputChange = useCallback((field, value) => {
    setFeedback(prev => ({ ...prev, [field]: value }))
    if (error) setError('')
  }, [error])

  const handleClose = useCallback(() => {
    if (!loading) {
      setError('')
      setSuccess('')
      onClose()
    }
  }, [loading, onClose])

  // Memoized validation
  const isFormValid = useMemo(() => 
    feedback.subject.trim() && feedback.message.trim(),
    [feedback.subject, feedback.message],
  )

  // Memoized system info collector
  const getSystemInfo = useCallback(() => ({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    screenResolution: `${screen.width}x${screen.height}`,
    viewport: `${innerWidth}x${innerHeight}`,
    timestamp: new Date().toISOString(),
    url: location.href,
  }), [])

  const validateForm = useCallback(() => {
    if (!feedback.subject.trim()) {
      setError('Please provide a subject for your feedback.')
      return false
    }
    if (!feedback.message.trim()) {
      setError('Please provide a detailed message.')
      return false
    }
    return true
  }, [feedback.subject, feedback.message])

  const submitFeedback = useCallback(async () => {
    if (!validateForm()) return

    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const feedbackData = {
        ...feedback,
        userId: user?.uid ?? 'anonymous',
        userEmail: user?.email ?? 'not-provided',
        timestamp: new Date().toISOString(),
        systemInfo: getSystemInfo(),
        status: 'new',
        source: 'help-support-page',
        to: ['technology@revatek.com'],
        template: {
          name: 'feedback-notification',
          data: {
            category: feedback.category,
            subject: feedback.subject,
            message: feedback.message,
            userEmail: user?.email ?? 'not-provided',
            timestamp: new Date().toLocaleString(),
          },
        },
      }

      await addDoc(collection(db, 'userFeedback'), feedbackData)

      setSuccess('Thank you for your feedback! We\'ll review it and get back to you if needed.')
      
      setTimeout(() => {
        setFeedback({ category: 'general', subject: '', message: '' })
        setSuccess('')
        onClose()
      }, 2000)

    } catch (submitError) {
      console.error('Error submitting feedback:', submitError)
      setError('Failed to submit feedback. Please try again or contact support directly.')
    } finally {
      setLoading(false)
    }
  }, [feedback, user, validateForm, getSystemInfo, onClose])

  const handleFormSubmit = useCallback((e) => {
    e.preventDefault()
    submitFeedback()
  }, [submitFeedback])

  if (!isOpen) return null

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={handleClose}
      size="large"
      animation="slideUp"
      zIndex="var(--z-modal)"
    >
      <div style={{ maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Modal Header */}
        <div 
          style={{
            padding: '20px 24px 16px 24px',
            borderBottom: '1px solid var(--border-color-light-gray)',
            background: 'var(--bg-header-gradient)',
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: 'var(--text-dark)',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}>
            <i className="bi bi-chat-square-text"></i>
            Send Feedback
          </h2>
          <button 
            onClick={handleClose}
            disabled={loading}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              color: 'var(--text-light-muted)',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => {
              e.target.style.color = 'var(--text-medium)'
              e.target.style.backgroundColor = 'var(--color-hover-dark)'
            }}
            onMouseLeave={(e) => {
              e.target.style.color = 'var(--text-light-muted)'
              e.target.style.backgroundColor = 'transparent'
            }}
            aria-label="Close"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>

        {/* Modal Content - Scrollable */}
        <div style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '24px',
          background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-light-blue) 100%)',
        }}>
          {/* Alert Messages */}
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError('')} />
            </div>
          )}

          {success && (
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="bi bi-check-circle me-2"></i>
              {success}
            </div>
          )}

          {/* Feedback Form */}
          <form onSubmit={handleFormSubmit}>
            {/* Category Selection */}
            <div className="mb-4">
              <label className="form-label fw-semibold text-dark">
                What type of feedback is this?
              </label>
              <div className="row g-3">
                {FEEDBACK_CATEGORIES.map((category) => (
                  <div key={category.value} className="col-lg-4 col-md-6 col-12">
                    <div 
                      className={`form-check p-3 border rounded ${
                        feedback.category === category.value 
                          ? 'bg-primary bg-opacity-10 border-primary' 
                          : 'bg-white border-light'
                      }`}
                    >
                      <input
                        className="form-check-input"
                        type="radio"
                        id={`category_${category.value}`}
                        name="category"
                        value={category.value}
                        checked={feedback.category === category.value}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                      />
                      <label className="form-check-label small" htmlFor={`category_${category.value}`}>
                        <i className={`bi ${category.icon} me-2 text-muted`} />
                        {category.label}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div className="mb-3">
              <label htmlFor="feedbackSubject" className="form-label fw-semibold text-dark">
                Subject <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control border-light rounded"
                id="feedbackSubject"
                placeholder="Brief summary of your feedback"
                value={feedback.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                maxLength={CHAR_LIMITS.subject}
                style={{ padding: '12px', fontSize: '0.875rem' }}
              />
              <div className="form-text small text-muted">
                {feedback.subject.length}/{CHAR_LIMITS.subject} characters
              </div>
            </div>

            {/* Message */}
            <div className="mb-3">
              <label htmlFor="feedbackMessage" className="form-label fw-semibold text-dark">
                Message <span className="text-danger">*</span>
              </label>
              <textarea
                className="form-control border-light rounded"
                id="feedbackMessage"
                rows="4"
                placeholder="Please provide detailed feedback, including steps to reproduce any issues..."
                value={feedback.message}
                onChange={(e) => handleInputChange('message', e.target.value)}
                maxLength={CHAR_LIMITS.message}
                style={{ padding: '12px', fontSize: '0.875rem', resize: 'vertical' }}
              />
              <div className="form-text small text-muted">
                {feedback.message.length}/{CHAR_LIMITS.message} characters
              </div>
            </div>
          </form>
        </div>
        
        {/* Footer */}
        <div className="d-flex justify-content-end gap-3 px-4 py-3 border-top bg-light rounded-bottom">
          <button 
            type="button" 
            className="btn btn-outline-secondary rounded fw-medium" 
            onClick={handleClose}
            disabled={loading}
            style={{ padding: '8px 16px', fontSize: '0.875rem' }}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary rounded fw-medium" 
            onClick={submitFeedback}
            disabled={loading || !isFormValid}
            style={{ padding: '8px 20px', fontSize: '0.875rem' }}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                Sending...
              </>
            ) : (
              <>
                <i className="bi bi-send me-2" />
                Send Feedback
              </>
            )}
          </button>
        </div>
      </div>
    </ModalPortal>
  )
}

SendFeedback.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SendFeedback
